import { blocksToMarkdown, PartialBlock } from '@blocknote/core';
import { clone } from 'radash';
import appLogger from '@/services/logger';

function flattenSpacesWithinTextNode(textNode) {
  const text = textNode.text;
  const matches = text.match(/^(\s*)(.*?)(\s*)$/);

  if (!matches) {
    return [textNode];
  }

  const [, leadingSpaces, mainText, trailingSpaces] = matches;
  const nodes: any[] = [];

  if (leadingSpaces) {
    nodes.push({ type: 'text', text: leadingSpaces, styles: {} });
  }

  if (mainText) {
    nodes.push({
      type: 'text',
      text: mainText,
      styles: textNode.styles
    });
  }

  if (trailingSpaces) {
    nodes.push({ type: 'text', text: trailingSpaces, styles: {} });
  }

  return nodes;
}

function processTextContent(content) {
  return content.flatMap(item => {
    if (item.type === 'text') {
      return flattenSpacesWithinTextNode(item);
    }
    return [item];
  });
}

function tryProcessDocument(editorDocument: PartialBlock[]): PartialBlock[] {
  const blocks = clone(editorDocument);

  blocks.forEach(block => {
    if (
      block.type === 'paragraph' ||
      block.type === 'heading' ||
      block.type === 'bulletListItem' ||
      block.type === 'checkListItem' ||
      block.type === 'numberedListItem' ||
      block.type === ('titleBlock' as any)
    ) {
      block.content = processTextContent(block.content ?? []);
    }
  });

  return blocks;
}

async function getTitleFromDocument(editor, editorDocument: PartialBlock[]): Promise<string> {
  const titleBlock = editorDocument.find(block => block.type === ('titleBlock' as any));
  const content = titleBlock?.content;

  if (!Array.isArray(content) || content.length === 0) {
    return '';
  }

  const titleAsParagraph: PartialBlock = { type: 'paragraph', content };
  const titleMarkdown = await blocksToMarkdown([titleAsParagraph], editor.pmSchema, editor, {});
  return titleMarkdown.trim();
}

const getFilteredContentFromDocument = (editorDocument: PartialBlock[]) => {
  return editorDocument.filter(block => block.type !== ('titleBlock' as any));
};

export const convertToBlockNoteMarkdown = async editor => {
  let contentBlocks;
  let title = '';

  try {
    const processedDocument = tryProcessDocument(editor.document);
    title = await getTitleFromDocument(editor, processedDocument);
    contentBlocks = getFilteredContentFromDocument(processedDocument);
  } catch (error) {
    appLogger.error('Error processing document, fallback to original document', error);
    title = await getTitleFromDocument(editor, editor.document);
    contentBlocks = getFilteredContentFromDocument(editor.document);
  }

  const markdown = await blocksToMarkdown(contentBlocks, editor.pmSchema, editor, {});
  return { title, markdownBody: markdown };
};
